import dayjs from 'dayjs';
import { DateString } from '../../../utils/type';
import {
  ApiMessage,
  ApiResponseHandler,
  UpdateEmployeeData,
} from '../../ApiResponseHandler';
import ThomApiClient from '../../ThomApiClient';
import { ProgramEmployeeResponse } from '../../programManagment/programManagment';
import { ActivitySheet } from '../activitySheet/weeklyActivitySheetProvider';
import { StatusType } from '../../../pages/activitySheet/ActivityTableRow';
import { STORAGE_USER_ID_KEY } from '../../Constant';
import { EmployeeListType } from '../../ifspClient/IfspApi';

export interface LocalProgram {
  id: string;
  programId: string | null;
  programName: string | null;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  // editable: boolean;
  acronym: string | null;
  startDate: string | null;
  endDate: string | null;
  staffType: string | null;

}

export interface EmployeeDisciplineHistory {
  fId: string;
  id: string | null; // null for new record, has value for update
  disciplineType: string | null;
  credentialLicenseNumber: string | null;
  credentialExpirationDate: DateString | null;
  credentialFileUrl: string | null;
  startDate: DateString | null;
  isChanged: boolean;
  isDeleted: boolean;
}

export interface LocalEmployeeCredentials {
  credentialLicenseNumber: string | null;
  credentialExpirationDate: DateString | null;
  credentialFileUrl: string | null;
}

export interface BlockTimeData {
  blockTimeId?: string;
  blockingType: string;
  description?: string;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  duration: string | null;
  occurrenceDays: string[];
  occurrenceDates: {
    start: string;
    end: string;
  }[];
}
export interface BlockTimeDataList {
  blockTimeDetails: BlockTimeData[];
  totalBlockTimeCount: number;
}

export interface GroupEmployee {
  id: string | null;
  groupName: string | null;
}

export interface Employee {
  nameSuffix: string;
  id: string;
  clientId: string | null;
  kcUsername: string | null;
  firstName: string | null;
  lastName: string | null;
  interpreter?: boolean;
  middleName: string | null;
  preferredName: string | null;
  email: string | null;
  phone1: string | null;
  phone2: string | null;
  workingHoursPerWeek: string | null;
  productivityGoal: string | null;
  allowProductivityGoalEdit: boolean | null;
  employeeRole: string | null;
  reportingTo: string | null;
  employeeType: string | null;
  startDate: string | null;
  endDate: string | null;
  name: string | null;
  status: string | null;
  primaryAddress1: string | null;
  primaryAddress2: string | null;
  primaryZipcode: string | null;
  primaryCity: string | null;
  primaryState: string | null;
  secondaryAddressRequired: boolean;
  secondaryAddress1: string | null;
  secondaryAddress2: string | null;
  secondaryZipcode: string | null;
  secondaryCity: string | null;
  secondaryState: string | null;

  emergencyFirstName: string | null;
  emergencyLastName: string | null;
  emergencyPhone: string | null;
  emergencyEmail: string | null;

  workingHoursPerWeekStartDate?: DateString;
  productivityGoalStartDate?: DateString;
  employeeTypeStartDate?: DateString;
  lunchWaiverRequired: boolean;
  lunchWaiverStartDate: DateString;

  emergencyAddress1: string | null;
  emergencyAddress2: string | null;
  emergencyZipcode: string | null;
  emergencyCity: string | null;
  emergencyState: string | null;
  programs: LocalProgram[] | null;
  modifiedOn?: string | null;
  employeeCredentials: LocalEmployeeCredentials | null;
  allowDelete: boolean | null;
  disciplineHistoryList?: EmployeeDisciplineHistory[] | null;
  signature: string;
  pin: string | number | null;

  signatureRowKey?: string;
  signaturePartitionKey?: string;

  phoneNumberExtension: string;
  phoneNumberExtension2: string;
  defaultProgram: string;
  superProgramId: string | null;
  disciplineType: string | null;
  group: GroupEmployee;
  npi: string | null;

  endDateUpdated?: boolean;
  activitySheetUpdated?: boolean;
  isRehireChecked?: boolean;
  rehireMonths?: number|null;
}

export interface EmployeeFilter {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  disciplineType: string | null;
  roles: string[] | null;
  phoneNumberExtension: string;
  isInActive?: boolean;
}

export interface Schedule {
  scheduleId: string | null;
  day: number;
  lunchStartTime: string | null;
  lunchEndTime: string | null;
  startTime: string | null;
  endTime: string | null;
}

export interface BusinesScheduleResponse {
  myWeekSchedule: {
    0: Schedule | null;
    1: Schedule | null;
    2: Schedule | null;
    3: Schedule | null;
    4: Schedule | null;
    5: Schedule | null;
    6: Schedule | null;
  };
  workingHours: string | null;
  scheduledHours: string | null;
}

export interface EmployeesResponse {
  employeeCount: number;
  employees: Employee[];
}

export interface TaskManagerItem {
  id: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  clientId: string | null;
  name: string | null;
  dueDate: string | null;
  status: string | null;
}

export interface UpcommingEventItem {
  id: string | null;
  date: DateString;
  day: string | null;
  type: string | null;
}

export interface CaseLoadClient {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  assessmentHoursDec: number | null;
  serviceCoordinator: string | null;
}

export interface ProgressNoteItem {
  id: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  dphId: string | null;
  appointmentId: string | null;
  commonAppointmentId: string | null;
  clientId: string | null;
  status: StatusType;
}

export interface CaseLoadList {
  noOfClients: number | null;
  clientsAbove30: number | null;
  unscheduledClients: number | null;
  caseLoadClients: CaseLoadClient[];
}

export interface EmployeeDashboardResponse {
  activityAndProductivity: ActivitySheet;
  taskManager: TaskManagerItem[];
  upcomingEvents: UpcommingEventItem[];
  caseLoad: CaseLoadList;
  progressNotes: ProgressNoteItem[];
}

export interface EmployeeDashboardResponseOffline {
  upcomingEvents: UpcommingEventItem[];
  progressNotes: ProgressNoteItem[];
}

export const getEmployeeDashboardDetails =
  async (): Promise<EmployeeDashboardResponse> => {
    const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
    return ApiResponseHandler(
      await ThomApiClient.getInstance().get<EmployeeDashboardResponse>(
        `employee/dashboard?timezone=${timezone}`,
      ),
    );
  };

export const getEmployeeList = async (
  search: string,
  page: number,
  size: number,
  sort: string,
  sortType: string,
  status: string[],
): Promise<EmployeesResponse> => {
  const required = false;
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<EmployeesResponse>(
      `employee?search=${search}&page=${page}&size=${size}&sort=${sort}&status=${status}&sortType=${sortType.toUpperCase()}&allEmployeesRequired=${required}&timezone=${timezone}`,
    ),
  );
};

export const fetchActiveEmployees = async (
  employeeIds?: string[],
): Promise<EmployeeFilter[]> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<EmployeeFilter[]>(
      `employee/list?employeeIds=${employeeIds || []}`,
    ),
  );
};

export const getEmployeeById = async (
  employeeId: string,
): Promise<Employee> => {
  const userIdCurrent = localStorage.getItem(STORAGE_USER_ID_KEY);
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<Employee>(
      userIdCurrent !== employeeId
        ? `employee/details?employeeId=${employeeId}&timezone=${timezone}`
        : `employee/my-profile?employeeId=${employeeId}&timezone=${timezone}`,
    ),
  );
};

export const getEmployeeByIdForProfile = async (
  employeeId: string,
): Promise<Employee> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<Employee>(
      `employee/my-profile?employeeId=${employeeId}&timezone=${timezone}`,
    ),
  );
};

export const getBusinessSchedules = async (
  employeeId: string,
): Promise<BusinesScheduleResponse> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<BusinesScheduleResponse>(
      `schedule?employeeId=${employeeId}`,
    ),
  );
};

export const createOrUpdateBusinessSchedules = async (
  employeeId: string,
  schedule: Schedule,
): Promise<ApiMessage> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  if (schedule.scheduleId) {
    return ApiResponseHandler(
      await ThomApiClient.getInstance().put<Schedule, ApiMessage>(
        `schedule?timezone=${timezone}`,
        schedule,
      ),
    );
  } else {
    return ApiResponseHandler(
      await ThomApiClient.getInstance().post<Schedule, ApiMessage>(
        `schedule?employeeId=${employeeId}&timezone=${timezone}`,
        schedule,
      ),
    );
  }
};

export const deleteBusinessSchedules = async (
  scheduleId: string,
): Promise<ApiMessage> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().delete<ApiMessage>(
      `schedule?scheduleId=${scheduleId}&timezone=${timezone}`,
    ),
  );
};

export const updateEmployeeDataByAdmin = async (
  employee: Employee,
): Promise<UpdateEmployeeData> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<Employee, UpdateEmployeeData>(
      `employee?timezone=${timezone}`,
      employee,
    ),
  );
};

export const updateEmployeeDataByUser = async (
  employee: Employee,
): Promise<UpdateEmployeeData> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<Employee, UpdateEmployeeData>(
      `employee/my-profile?timezone=${timezone}`,
      employee,
    ),
  );
};

export const getAllEmployeeList = async (
  startDateTime: string,
): Promise<Employee[]> => {
  const timezone = encodeURIComponent(dayjs(startDateTime).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<Employee[]>(
      `attendee/list?startDateTime=${startDateTime}&timezone=${timezone}`,
    ),
  );
};

// export const getEmployeeLookup = async (
//   page: number,
//   size: number,
//   sort: string,
//   sortType: string,
//   searchquery: string,
//   programName: string,
// ) => {
//   const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
//   return ApiResponseHandler(
//     await ThomApiClient.getInstance().get<EmployeeLookup>(
//       `employee/lookup?page=${page}&size=${size}&sort=${sort}&sortType=${sortType}&search=${searchquery}&program=${programName}&timezone=${timezone}`,
//     ),
//   );
// };

// export const getAllEmployee = async (
//   page: number,
//   size: number,
//   sort: string,
//   sortType: string,
//   searchquery: string,
// ) => {
//   const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
//   return ApiResponseHandler(
//     await ThomApiClient.getInstance().get<EmployeeLookup>(
//       `employee?page=${page}&size=${size}&sort=${sort}&sortType=${sortType.toUpperCase()}&search=${searchquery}&timezone=${timezone}`,
//     ),
//   );
// };

export const getProgramEmployees = async (
  page: number,
  order: string,
  orderBy: string,
  searchquery: string,
  program: string,
  allEmployeesRequired: boolean,
) => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<ProgramEmployeeResponse>(
      `employee/lookup?page=${page}&size=10&sort=${orderBy.toUpperCase()}&sortType=${order}&search=${searchquery}&program=${program}&timezone=${timezone}&allEmployeesRequired=${allEmployeesRequired}`,
    ),
  );
};

export const addEmployeeBlockTime = async (
  employeeId: string,
  requestBody: BlockTimeData,
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post<BlockTimeData, ApiMessage>(
      `block-time?employeeId=${employeeId}`,
      requestBody,
    ),
  );
};

export const getEmployeeBlockTime = async (
  employeeId: string,
  pageNo: number,
  year: string,
): Promise<BlockTimeDataList> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<BlockTimeDataList>(
      `block-time?employeeId=${employeeId}&page=${pageNo}&size=5&filter=${year}`,
    ),
  );
};
export const deleteEmployeeBlockTime = async (
  blockingTimeId: string,
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().delete<ApiMessage>(
      `block-time?blockTimeId=${blockingTimeId}`,
    ),
  );
};

export const updateEmployeeBlockTime = async (
  requestBody: BlockTimeData,
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<BlockTimeData, ApiMessage>(
      `block-time`,
      requestBody,
    ),
  );
};

export const uploadEmployeeProfileOrCredentialFile = async (
  imageFile: File | null,
  pdfFile: File | null,
  employeeId: string,
  disciplineId: string | null | undefined,
): Promise<ApiMessage> => {
  const formData = new FormData();
  if (imageFile !== null) {
    formData.append('imageFile', imageFile);
  } else {
    formData.append('imageFile', '');
  }
  if (pdfFile !== null) {
    formData.append('pdfFile', new File([pdfFile], pdfFile.name.toLowerCase()));
  } else {
    formData.append('pdfFile', '');
  }
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post(
      `employee/uploadFile?employeeId=${employeeId}&disciplineHistoryId=${disciplineId}`,
      formData,
    ),
  );
};

export const fileDelete = async (
  employeeId: string,
  imageFile: boolean,
  pdfFile: boolean,
  disciplineHistoryId: string | null,
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().delete<ApiMessage>(
      `employee/deleteFile?employeeId=${employeeId}&imageFile=${imageFile}&pdfFile=${pdfFile}&disciplineHistoryId=${disciplineHistoryId}`,
    ),
  );
};

export const activitySheetCheck = async (
  employee: Employee,
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post<Employee, ApiMessage>(
      `/activity/exists`,
      employee,
    ),
  );
};

export const getAllEmployeeByName = async (
  search: string,
  page: number,
  size: number,
  empIds: string[],
): Promise<EmployeeListType[]> => {
  console.log('empIds', empIds, 'search', search, 'page', page, 'size', size);
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<EmployeeListType[]>(
      `employee/listByName?search=${search}&empIds=${empIds}&page=${page}&size=${size}`,
    ),
  );
};
